import { UserType } from 'src/type/user';

import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Stack,
  Dialog,
  Button,
  Divider,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import useProductOptions from 'src/hooks/use-product-options';
import useBranchOptions from 'src/hooks/use-branch-option';
import dayjs, { Dayjs } from 'dayjs';
import { fNumber } from 'src/utils/format-number';
import useProductFeeOptions from 'src/hooks/use-product-fee-options';
import Iconify from 'src/components/iconify';
import axios, { endpoints } from 'src/utils/axios';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  GoldWithdrawFilters,
  GoldWithdrawFilterKey,
  GoldWithdrawFilterValue,
} from 'src/type/gold-withdraw';
import { useState, useEffect, useCallback } from 'react';
import UserSearchAutocomplete, { UserOption } from 'src/components/ui-kit/user-search-autocomplete';
import { useBoolean } from 'src/hooks/use-boolean';
// import { BranchType } from 'src/type/branch';
// import { Pagination } from 'src/type/utils';
// import { DepositTxType } from 'src/type/deposit';
import useCurrentDateTime from 'src/hooks/use-current-date-time';

const defaultFilters: GoldWithdrawFilters = {
  name: null,
  productId: 'all',
  startDate: null,
  endDate: null,
  status: 'all',
};

const defaultValues = {
  productId: '',
  amount: 0,
  branchId: '',
  appointmentDate: null as unknown as Dayjs,
  appointmentTime: null as unknown as Dayjs,
  productRedeemFees: [{ id: '', amount: 0 }],
  depositCostPerUnit: 0,
  unitType: 'BAHT',
  ticketCreatedAt: dayjs() as unknown as Dayjs,
  ticketNumber: '',
};

const Schema = Yup.object().shape({
  productId: Yup.string().required('กรุณาเลือกสินค้า'),
  amount: Yup.number()
    .typeError('น้ำหนักต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุน้ำหนัก')
    .moreThan(-1, 'น้ำหนักขั้นต่ำ 0'),
  branchId: Yup.string().required('กรุณาเลือกสาขา'),
  appointmentDate: Yup.mixed<Dayjs>().nullable(),
  appointmentTime: Yup.mixed<Dayjs>().nullable(),
  productRedeemFees: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required('จำเป็นต้องระบุค่าธรรมเนียม'),
      amount: Yup.number()
        .typeError('จำนวนต้องเป็นตัวเลข')
        .required('จำเป็นต้องระบุจำนวน')
        .moreThan(0, 'จำนวนขั้นต่ำ 0.1'),
    })
  ),
  depositCostPerUnit: Yup.number()
    .typeError('ราคาต่อหน่วยต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุราคาต่อหน่วย')
    .moreThan(-1, 'ราคาต่อหน่วยขั้นต่ำ 0'),
  unitType: Yup.string().required('กรุณาเลือกหน่วยของทองคำ'),
  ticketCreatedAt: Yup.mixed<Dayjs>().nullable().required('กรุณาระบุวันที่ออกตั๋ว'),
  ticketNumber: Yup.string().required('กรุณาระบุเลขที่ตั๋ว'),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType | null;
  isCreateAndApprove?: boolean;
  handleReload: VoidFunction;
  openApproveDialog?: (txId?: string) => void;
};

export default function DepositCreateDialog({
  open,
  onClose,
  user,
  isCreateAndApprove = false,
  handleReload,
  openApproveDialog,
}: Props) {
  const currentDateTime = useCurrentDateTime();
  const [filters, setFilters] = useState(defaultFilters);
  const isDevSubmitting = useBoolean(false);

  const handleFilters = useCallback(
    (name: GoldWithdrawFilterKey, value: GoldWithdrawFilterValue) => {
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    []
  );

  const handleFilterName = useCallback(
    (userOption: UserOption | null) => {
      handleFilters('name', userOption);
    },
    [handleFilters]
  );

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    control,
    watch,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productRedeemFees',
  });

  const handleAdd = () => {
    append({
      id: '',
      amount: 0,
    });
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  const { productOptions } = useProductOptions(true, user?.uid);
  const selectedProduct = productOptions.find((item) => item.value === watch().productId);
  const isBar96 =
    selectedProduct &&
    selectedProduct?.label.includes('แท่ง') &&
    selectedProduct?.label.includes('96.5');

  const { branchOptions } = useBranchOptions();

  // Check if default values are still loading
  const isLoadingDefaults = open && (productOptions.length === 0 || branchOptions.length === 0);
  const { productFeeOptions, isProductFeeOptionsLoading } = useProductFeeOptions(watch().productId);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (totalWeight !== Number(values.amount)) {
        throw new Error('ขนาดทองไม่ตรงกับจำนวนทองที่ต้องการแลกเปลี่ยน');
      }

      let combinedDateTime: string | null = null;
      if (values.appointmentDate && values.appointmentTime) {
        combinedDateTime = dayjs(values.appointmentDate)
          .set('hour', dayjs(values.appointmentTime).hour())
          .set('minute', dayjs(values.appointmentTime).minute())
          .toISOString();
      } else if (values.appointmentDate) {
        combinedDateTime = dayjs(values.appointmentDate).toISOString();
      } else {
        combinedDateTime = null;
      }

      const uid = user?.uid || filters.name?.value;

      if (!uid) {
        throw new Error('ไม่พบข้อมูลลูกค้า');
      }

      const body = {
        uid,
        productId: values.productId,
        amount: Number(values.amount),
        branchId: values.branchId,
        appointmentDate: combinedDateTime,
        productRedeemFees: values.productRedeemFees,
        depositCostPerUnit: Number(values.depositCostPerUnit),
        currentDateTime,
        unitType: values.unitType,
        ticketCreatedAt: values.ticketCreatedAt
          ? dayjs(values.ticketCreatedAt).toISOString()
          : null,
        ticketNumber: values.ticketNumber,
      };

      const { data } = await axios.post(endpoints.transactions.deposit.create, body);

      if (isCreateAndApprove && openApproveDialog && data) {
        openApproveDialog(data.id);
      }

      if (!isCreateAndApprove) {
        handleReload();
        enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      }

      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  // const handleDevDeposit = async () => {
  //   try {
  //     isDevSubmitting.onTrue();

  //     // TODO: remove this after testing

  //     // clear all data
  //     await axios.get('/playground/set-all-user-product-amount-to-zero');

  //     // Get first branch for approve
  //     const { data: branchData } = await axios.get<Pagination<BranchType>>(endpoints.branch.get, {
  //       params: { limit: 1 },
  //     });

  //     if (!branchData.results || branchData.results.length === 0) {
  //       throw new Error('ไม่พบข้อมูลสาขา');
  //     }

  //     const firstBranchId = branchData.results[0].id;

  //     const body = {
  //       uid: 'rS3iMKwNOiYMzr6e2MV0m9fbD6k2',
  //       productId: '9FhTZkU6ssH60tPIcB3k',
  //       amount: 5,
  //       branchId: firstBranchId,
  //       appointmentDate: null,
  //       productRedeemFees: [
  //         {
  //           id: 'J4kTlsi42pqyovlHnTmD',
  //           amount: 2,
  //         },
  //         {
  //           id: 'pnpxcnwAEl6kuirDxpv2',
  //           amount: 1,
  //         },
  //       ],
  //       depositCostPerUnit: 30000,
  //       currentDateTime,
  //       unitType: 'BAHT',
  //       ticketCreatedAt: dayjs('2026-01-09T19:58:22.655Z').toISOString(),
  //       ticketNumber: 'GOLD123',
  //     };

  //     // Create deposit transaction
  //     const { data: depositResponse } = await axios.post<{ id: string } | DepositTxType>(
  //       endpoints.transactions.deposit.create,
  //       body
  //     );

  //     if (!depositResponse?.id) {
  //       throw new Error('ไม่พบ transaction id จาก response');
  //     }

  //     // Approve deposit if isCreateAndApprove is true
  //     if (isCreateAndApprove) {
  //       await axios.post(endpoints.transactions.deposit.approve(depositResponse.id), {
  //         note: '',
  //         currentDateTime,
  //       });
  //     }

  //     handleReload();
  //     beforeClose();
  //     enqueueSnackbar('สร้างและอนุมัติรายการสำเร็จ (Dev)', { variant: 'success' });
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     isDevSubmitting.onFalse();
  //   }
  // };

  const feeWithInfo = watch().productRedeemFees?.map((fee) => {
    const redeemFee = productFeeOptions.find((option) => option.value === fee.id)?.info;
    const weight =
      watch().unitType === 'BAHT' ? redeemFee?.weightBaht || 0 : redeemFee?.weightGram || 0;
    return {
      ...fee,
      weight,
    };
  });

  const totalWeight = feeWithInfo?.reduce((sum, item) => sum + item.amount * item.weight, 0) || 0;

  useEffect(() => {
    if (!isBar96) {
      setValue('unitType', 'GRAM');
    }
  }, [isBar96]);

  useEffect(() => {
    if (open) {
      // Set default productId to first option if available and form is empty
      if (productOptions.length > 0 && !getValues('productId')) {
        const firstProduct = productOptions[0];
        setValue('productId', firstProduct.value);
        // If default product includes "แท่ง" and "96.5", set unitType to "BAHT"
        if (firstProduct.label.includes('แท่ง') && firstProduct.label.includes('96.5')) {
          setValue('unitType', 'BAHT');
        }
      }
      // Set default branchId to first option if available and form is empty
      if (branchOptions.length > 0 && !getValues('branchId')) {
        setValue('branchId', branchOptions[0].value);
      }
      // Set default ticketCreatedAt to current date if empty
      if (!getValues('ticketCreatedAt')) {
        setValue('ticketCreatedAt', dayjs() as unknown as Dayjs);
      }
    }
  }, [open, productOptions, branchOptions, setValue, getValues]);

  return (
    <Dialog
      open={open}
      onClose={beforeClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างรายการฝากสินค้า</DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
          }}
        >
          <Stack spacing={{ xs: 2, sm: 3 }} py={1}>
            {(!user || user === null) && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                flexGrow={1}
                sx={{ width: { xs: 1, sm: 'auto' }, minWidth: 140 }}
              >
                <UserSearchAutocomplete
                  label="ค้นหาด้วยชื่อลูกค้า"
                  value={filters.name}
                  onChange={handleFilterName}
                />
              </Stack>
            )}

            <RHFSelect
              name="productId"
              label="สินค้า"
              placeholder="เลือกสินค้า"
              disabled={isLoadingDefaults}
            >
              {productOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <Stack
              spacing={2}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <RHFSelect
                name="unitType"
                label="หน่วยทองคำ"
                disabled={isLoadingDefaults || !isBar96}
                // sx={{ minWidth: { xs: '100%', sm: 140 } }}
              >
                <MenuItem value="BAHT">บาท</MenuItem>
                <MenuItem value="GRAM">กรัม</MenuItem>
              </RHFSelect>
              <RHFTextField
                name="amount"
                type="number"
                label="น้ำหนัก"
                placeholder="0"
                disabled={isLoadingDefaults}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color="success.main">
                        {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: { xs: 1, sm: '1 1 auto' } }}
              />
            </Stack>

            <RHFTextField
              name="depositCostPerUnit"
              type="number"
              label="ราคาต่อหน่วย (1 บาท)"
              placeholder="0"
              disabled={isLoadingDefaults}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight="bold" color="success.main">
                      THB
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            <RHFSelect
              name="branchId"
              label="สาขา"
              placeholder="เลือกสาขา"
              disabled={isLoadingDefaults}
            >
              {branchOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            {/* --- เพิ่มวันที่ออกตั๋วและเลขที่ตั๋วไว้ใต้ "สาขา" --- */}
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="ticketCreatedAt"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      label="วันที่ออกตั๋ว"
                      value={getValues('ticketCreatedAt')}
                      onChange={(newValue) => field.onChange(newValue)}
                      format="DD/MM/YYYY"
                      disabled={isLoadingDefaults}
                      sx={{ width: 1 }}
                      slotProps={{
                        textField: {
                          error: Boolean(fieldState.error),
                          helperText: fieldState.error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
              <RHFTextField
                name="ticketNumber"
                label="เลขที่ตั๋ว"
                placeholder="ระบุเลขที่ตั๋ว"
                required
                disabled={isLoadingDefaults}
              />
            </Stack>
            {/* --- end เพิ่มวันที่ออกตั๋วและเลขที่ตั๋ว --- */}

            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
              <Controller
                name="appointmentDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="วันนัดหมาย"
                    value={getValues('appointmentDate')}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    format="DD/MM/YYYY"
                    disabled={isLoadingDefaults}
                    sx={{ width: 1 }}
                  />
                )}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="appointmentTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เวลานัดหมาย"
                      value={getValues('appointmentTime')}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      disabled={isLoadingDefaults}
                      sx={{ width: 1 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Stack>
            <Stack spacing={3}>
              <Typography variant="caption" color="action.disabled">
                ขนาดทอง
              </Typography>
              <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={2}>
                {fields.map((item, index) => (
                  <Stack key={item.id} spacing={1.5} alignItems="flex-end">
                    <Grid container spacing={2} sx={{ width: 1 }}>
                      <Grid item xs={12} sm={8}>
                        <RHFSelect
                          size="small"
                          name={`productRedeemFees[${index}].id`}
                          label="ทอง"
                          InputLabelProps={{ shrink: true }}
                          placeholder="ทอง"
                          disabled={
                            isLoadingDefaults || isProductFeeOptionsLoading || !watch().productId
                          }
                          fullWidth
                        >
                          {productFeeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                        >
                          <RHFTextField
                            size="small"
                            name={`productRedeemFees[${index}].amount`}
                            type="number"
                            label="จำนวน"
                            inputMode="numeric"
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            disabled={
                              isLoadingDefaults || isProductFeeOptionsLoading || !watch().productId
                            }
                            fullWidth
                            sx={{ flex: { xs: 1, sm: '1 1 auto' } }}
                          />
                          {fields.length > 1 && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                              onClick={() => handleRemove(index)}
                              disabled={isLoadingDefaults}
                              sx={{
                                mt: { xs: 0, sm: 0.5 },
                                width: { xs: '100%', sm: 'auto' },
                                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                              }}
                            >
                              ลบ
                            </Button>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                ))}
              </Stack>

              <Stack
                spacing={3}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Button
                  size="small"
                  color="primary"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={handleAdd}
                  sx={{
                    flexShrink: 0,
                    width: { xs: '100%', sm: 'auto' },
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  }}
                  disabled={isLoadingDefaults || isProductFeeOptionsLoading || !watch().productId}
                >
                  เพิ่ม
                </Button>

                <Stack
                  alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                  sx={{ width: { xs: 1, sm: 1 } }}
                >
                  <Typography
                    variant={totalWeight !== Number(watch().amount) ? 'subtitle1' : 'subtitle2'}
                    color={totalWeight !== Number(watch().amount) ? 'error' : 'success.main'}
                    sx={{ fontSize: { xs: '0.875rem', sm: 'inherit' } }}
                  >
                    น้ำหนักทองรวม {fNumber(totalWeight)}{' '}
                    {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: { xs: '0.75rem', sm: 'inherit' } }}
                  >
                    น้ำหนักทองที่ฝาก {fNumber(Number(watch().amount))}{' '}
                    {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                  </Typography>
                </Stack>
              </Stack>
              <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            alignItems: 'center',
          }}
        >
          {/* <LoadingButton
            variant="contained"
            onClick={handleDevDeposit}
            loading={isDevSubmitting.value}
            disabled={isSubmitting || isDevSubmitting.value}
            sx={{
              backgroundColor: 'error.main',
              '&:hover': { backgroundColor: 'error.dark' },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 100 },
            }}
          >
            dev-deposit
          </LoadingButton> */}
          <Button
            variant="outlined"
            onClick={beforeClose}
            disabled={isSubmitting || isDevSubmitting.value}
            fullWidth={false}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 100 },
            }}
          >
            ยกเลิก
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={
              isLoadingDefaults ||
              (isCreateAndApprove && !filters.name?.value && !user?.uid) ||
              isDevSubmitting.value
            }
            loading={isSubmitting}
            fullWidth
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 100 },
            }}
          >
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
